import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Landmark, AlertTriangle } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Card, PageHeader, Spinner, EmptyState } from "../../components/dashboard/DashboardShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { rupiah, fmtDateTime, PAYOUT_LABELS, PAYOUT_STYLES } from "../../lib/format";

export default function PayoutsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: w, isLoading } = useQuery({ queryKey: ["wallet"], queryFn: () => api.get("/wallet").then((r) => r.data) });
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  if (isLoading || !w) return <Spinner />;
  const hasBank = !!w.bank_account?.account_number;

  const submit = async () => {
    const n = Number(amount);
    if (!n || n <= 0) return toast.error("Masukkan jumlah yang valid");
    setBusy(true);
    try {
      await api.post("/wallet/payouts", { amount: n });
      toast.success("Permintaan pencairan dikirim. Menunggu persetujuan admin.");
      setAmount(""); qc.invalidateQueries({ queryKey: ["wallet"] }); qc.invalidateQueries({ queryKey: ["inspector-dashboard"] });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Pencairan Dana" subtitle="Tarik saldo Anda ke rekening bank terdaftar." />
      <div className="grid xl:grid-cols-3 gap-4">
        <Card>
          <p className="text-xs font-semibold text-slate-400">Saldo Tersedia</p>
          <p className="text-3xl font-extrabold text-slate-900 mt-1" data-testid="payout-balance">{rupiah(w.balance)}</p>
          {w.pending_payout > 0 && <p className="text-[11px] text-amber-600 font-semibold mt-1">{rupiah(w.pending_payout)} sedang diproses</p>}
          <div className="mt-5 rounded-xl border border-slate-100 p-3 flex items-start gap-3">
            <Landmark size={18} className="text-blue-600 mt-0.5" />
            {hasBank ? (
              <div className="text-xs"><p className="font-bold text-slate-800">{w.bank_account.bank} · {w.bank_account.account_number}</p><p className="text-slate-500">{w.bank_account.account_name}</p></div>
            ) : (
              <div className="text-xs"><p className="font-bold text-amber-600 flex items-center gap-1"><AlertTriangle size={12} /> Rekening belum diatur</p><button onClick={() => navigate("/inspector/profil")} className="text-blue-600 font-bold mt-0.5">Atur di Profil & Layanan →</button></div>
            )}
          </div>
          <div className="mt-5">
            <label className="text-xs font-bold text-slate-600">Jumlah pencairan (Rp)</label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Min. ${rupiah(w.min_payout)}`} className="mt-1.5 h-11" data-testid="payout-amount-input" />
            <div className="flex gap-1.5 mt-2">
              {[0.25, 0.5, 1].map((p) => <button key={p} onClick={() => setAmount(String(Math.floor(w.balance * p)))} className="text-[11px] font-bold text-blue-600 bg-blue-50 rounded-lg px-2.5 py-1">{p === 1 ? "Semua" : `${p * 100}%`}</button>)}
            </div>
            <Button onClick={submit} disabled={busy || !hasBank} data-testid="payout-submit-btn" className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl h-11 shadow-lg shadow-blue-600/25">
              {busy && <Loader2 size={16} className="animate-spin mr-2" />} Tarik Dana
            </Button>
            <p className="text-[11px] text-slate-400 mt-3">Pencairan diproses admin dalam 1×24 jam kerja.</p>
          </div>
        </Card>
        <Card title="Riwayat Pencairan" className="xl:col-span-2">
          {!w.payouts.length ? <EmptyState icon="banknote" title="Belum ada pencairan" /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[480px]" data-testid="payouts-table">
                <thead><tr className="text-left text-[11px] text-slate-400 font-bold border-b border-slate-100"><th className="pb-3 pr-3">Tanggal</th><th className="pb-3 pr-3">Jumlah</th><th className="pb-3 pr-3">Rekening</th><th className="pb-3 pr-3">Status</th><th className="pb-3">Catatan</th></tr></thead>
                <tbody>
                  {w.payouts.map((p) => (
                    <tr key={p.payout_id} className="border-b border-slate-50 last:border-0" data-testid={`payout-row-${p.payout_id}`}>
                      <td className="py-3.5 pr-3 text-slate-500 text-[13px]">{fmtDateTime(p.created_at)}</td>
                      <td className="py-3.5 pr-3 font-extrabold text-slate-800 text-[13px]">{rupiah(p.amount)}</td>
                      <td className="py-3.5 pr-3 text-slate-500 text-xs">{p.bank_account?.bank} {p.bank_account?.account_number}</td>
                      <td className="py-3.5 pr-3"><span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${PAYOUT_STYLES[p.status]}`}>{PAYOUT_LABELS[p.status]}</span></td>
                      <td className="py-3.5 text-xs text-slate-400">{p.note || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
